/**
 * A product block is the authored unit of "this copy belongs to product N".
 * It occupies whole lines and survives in `bodyText` as the email dialect's
 * `{% if items.<index> %}` guard — the same `items.N` the email editor exports
 * and the renderer runs — so a cart block pasted from an email round-trips:
 *
 *     Hey check out these deals
 *     {% if items.1 %}
 *     {{ items.1.name }} — {{ items.1.item_url }}
 *     {% endif %}
 *
 * The index is absolute for the block's model (see `productContexts.ts`), and
 * the guard is the entire degradation story: when item N isn't sourced, the
 * block drops itself instead of rendering blanks.
 *
 * The unit has to be authored rather than inferred: guessing it from line
 * breaks silently swallows unrelated copy when a product is referenced
 * mid-paragraph, and a missing item is a hard render failure, not a blank.
 */
const OPEN_PATTERN = /^\{%\s*if\s+items\.(\d+)\s*%\}$/;
const CLOSE_PATTERN = /^\{%\s*endif\s*%\}$/;

/** Opening marker for an absolute index, e.g. 1 → `{% if items.1 %}`. */
export function productBlockOpenMarker(index: number): string {
  return `{% if items.${index} %}`;
}

export const PRODUCT_BLOCK_CLOSE_MARKER = "{% endif %}";

/** Absolute index a line opens, or null when it isn't an opening marker. */
export function parseProductBlockOpen(line: string): number | null {
  const match = OPEN_PATTERN.exec(line.trim());
  if (!match) return null;
  const index = Number.parseInt(match[1], 10);
  return Number.isFinite(index) && index >= 0 ? index : null;
}

export function isProductBlockClose(line: string): boolean {
  return CLOSE_PATTERN.test(line.trim());
}
